/**
 * AppShell — signed-in two-column layout. Sticky 256px sidebar on the
 * left (brand + nav + premium badge); flexible main area on the right.
 * The shell is a layout route — actual page content renders via
 * <Outlet />.
 *
 * Auth gate: redirects to /auth/signin when unauthenticated. User +
 * profile load reactively via Convex and are available to every page
 * via a small context.
 */
import { Suspense, createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  type InboxResponse,
  type Profile, type User,
} from '@/lib/api';
import { useConvexAuth, useMutation, useQuery } from 'convex/react';
import { api as convexApi } from '../../../convex/functions/_generated/api';
import { Icons } from './Icons';
import { SignOutConfirm, useSignOutConfirm } from './SignOutConfirm';
import { SearchProfilesModal } from './SearchProfilesModal';

type AppCtx = {
  user: User;
  profile: Profile | null;
};

const Ctx = createContext<AppCtx | null>(null);

/** Hook for any page rendered inside AppShell to read the loaded user. */
export function useAppContext(): AppCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAppContext must be used inside <AppShell>');
  return v;
}

export function AppShell() {
  const navigate = useNavigate();
  // User + profile both reactive via Convex. The
  // imperative `api.me()` on mount raced Convex Auth's identity
  // propagation and caused the web equivalent of the mobile signin-
  // then-bounce loop. useConvexAuth + useQuery handles the race
  // for free (skip until auth confirms, then re-render when me()
  // resolves under the now-propagated identity).
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const me = useQuery(
    convexApi.users.me,
    isAuthenticated ? {} : 'skip',
  ) as User | null | undefined;
  const user: User | null = me ?? null;
  // Profile reads reactively too — Builder/Home edits flip without
  // an explicit refresh hook.
  const profileQuery = useQuery(
    convexApi.profiles.me,
    isAuthenticated ? {} : 'skip',
  ) as Profile | null | undefined;
  const profile: Profile | null = profileQuery ?? null;

  // Auth-gate redirect: bounce unauthenticated users to /auth/signin
  // ONLY after the hydration tick (isLoading=false) confirms it. The
  // global <AuthGate> in main.tsx does the same — this is belt-and-
  // suspenders for the AppShell-rooted routes.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/auth/signin');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Profile-complete redirect — reacts to me() resolving. New users
  // with no DOB / display_name yet route to /builder for onboarding.
  useEffect(() => {
    if (me && !me.profileComplete) {
      navigate('/builder');
    }
  }, [me, navigate]);

  // Shared unread-counts poll. Bell + DM badge + nav badge all read
  // from the SAME 30s interval — saves duplicate /notifications calls
  // when sidebar + top-bar mount concurrently on a mobile viewport
  // and keeps the badges in lockstep (no flicker race).
  //
  // MUST be called before any conditional return — React enforces
  // stable hook order between renders. The hook itself no-ops on auth
  // errors so calling it before `user` resolves is safe.
  const counts = useUnreadCounts();

  // Hydrating (Convex Auth + me() haven't both resolved) or
  // unauthenticated (the redirect effect above will fire on the next
  // tick) — render a generic Loading… shell so the screen doesn't
  // flash blank.
  if (authLoading || !isAuthenticated || me === undefined || !user) {
    return (
      <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)' }}>
        Loading…
      </main>
    );
  }

  return (
    <Ctx.Provider value={{ user, profile }}>
      <PresenceHeartbeat userId={user.id} />
      <ShellWithSearch user={user} counts={counts} />
    </Ctx.Provider>
  );
}

// ShellWithSearch — lifts the search-modal state above Sidebar +
// MobileTopBar so both surfaces can open it from their magnifying-
// glass buttons. The modal itself renders once at this level (portal)
// so it overlays whatever route is mounted in <Outlet />.
function ShellWithSearch({
  user, counts,
}: { user: User; counts: UnreadCounts }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = () => setSearchOpen(true);
  return (
    <>
      <div
        className="app-shell-grid"
        style={{
          minHeight: '100vh',
          display: 'grid',
          gridTemplateColumns: '256px 1fr',
          background: 'var(--bg)',
          color: 'var(--ink)',
        }}
      >
        <Sidebar user={user} counts={counts} onOpenSearch={openSearch} />
        <main className="app-shell-main" style={{ minWidth: 0, position: 'relative' }}>
          <MobileTopBar user={user} counts={counts} onOpenSearch={openSearch} />
          {/* Lazy routes (main.tsx) load here, inside the chrome. */}
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
          <MobileTabBar counts={counts} />
        </main>
      </div>
      <SearchProfilesModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

/**
 * PresenceHeartbeat — keeps the user marked online via @convex-dev/
 * presence whenever any AppShell-rooted page is open. Single global
 * roomId so `presence.statusFor(userId)` reflects app-wide presence.
 * Heartbeats every 10s; pauses while the tab is hidden (page-vis API)
 * and disconnects on unmount + on beforeunload so peers see us drop
 * off promptly when the tab closes.
 */
function PresenceHeartbeat({ userId }: { userId: string }) {
  const heartbeatMut = useMutation(convexApi.presence.heartbeat);
  const disconnectMut = useMutation(convexApi.presence.disconnect);
  useEffect(() => {
    const sessionId = `web-${userId}-${Math.random().toString(36).slice(2, 10)}`;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      heartbeatMut({
        roomId: 'global',
        userId,                     // server overrides with auth'd id
        sessionId,
        interval: 10_000,
      }).catch(() => null);
    };
    tick();
    const id = window.setInterval(tick, 10_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    const onBeforeUnload = () => {
      // Best-effort disconnect on tab close. fetch-keepalive would be
      // more reliable but Convex's mutation doesn't expose that knob.
      disconnectMut({ sessionToken: sessionId }).catch(() => null);
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('beforeunload', onBeforeUnload);
      disconnectMut({ sessionToken: sessionId }).catch(() => null);
    };
  }, [userId, heartbeatMut, disconnectMut]);
  return null;
}

// UnreadCounts is the breakdown the bell + messages-tab need. dm goes
// to the messages surface; other to the notifications bell. Kept as a
// flat object so consumers stay simple.
interface UnreadCounts { dm: number; other: number }

// useUnreadCounts subscribes to the notifications inbox via Convex's
// reactive query so the bell + messages-tab badges update the moment
// any markRead/markAllRead mutation lands or a new notification is
// inserted — no polling, no 30s tail. Returns zero for both while the
// query is loading so consumers render the no-badge case cleanly.
//
// The `BADGE_REFRESH_EVENT` window event ([…](DMThread.tsx),
// Notifications.tsx) is now redundant — the mutations those producers
// fire are Convex mutations that this useQuery already reacts to —
// but it's left as a harmless no-op safety net.
function useUnreadCounts(): UnreadCounts {
  const inbox = useQuery(
    convexApi.notifications.inbox,
    { category: 'all' },
  ) as InboxResponse | undefined;
  // The Messages nav badge combines unread inbox
  // threads with pending requests. dms.unreadThreadCount returns
  // `inboxUnreadThreads`/`requestsCount`; fall back to `count`
  // (legacy inbox-only) for the inbox half.
  const threadCounts = useQuery(convexApi.dms.unreadThreadCount, {}) as
    | { count: number; inboxUnreadThreads?: number; requestsCount?: number }
    | undefined;
  const dm = threadCounts
    ? (threadCounts.inboxUnreadThreads ?? threadCounts.count ?? 0) +
      (threadCounts.requestsCount ?? 0)
    : 0;
  const other = inbox ? inbox.unread_other_count : 0;
  return { dm, other };
}

/** Window-level event name. Any component can `window.dispatchEvent(
 *  new Event(BADGE_REFRESH_EVENT))` to nudge badge consumers. Used by
 *  DMThread on thread-mark-read + Notifications.tsx on mark-(all-)read.
 *  Largely redundant with the reactive inbox query — kept as a
 *  harmless safety net. */
export const BADGE_REFRESH_EVENT = 'wydm:badge-refresh';

// MobileTopBar — replaces the sidebar's brand + Notifications affordances
// on phones. Sticky at the top of the main column; hidden on desktop
// (>900px) via the .mobile-topbar default-display:none rule in base.css.
function MobileTopBar({ user, counts, onOpenSearch }: { user: User; counts: UnreadCounts; onOpenSearch: () => void }) {
  const unread = counts.other; // bell only counts non-DM events; DMs live on the messages tab
  const premium = user.tier === 'premium';

  return (
    <header
      className="mobile-topbar"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        display: 'none',
        alignItems: 'center',
        justifyContent: 'space-between',
        // 18px bottom pad replaces the old 1px hairline border —
        // content (the YOUR CARD row + photo carousel below) was
        // landing right on the border on the home screen which read
        // as a layout collision. The 90%-opaque bg + backdrop-blur
        // still provide enough separation when the page scrolls
        // underneath the sticky bar.
        padding: '14px 18px 18px',
        background: 'color-mix(in srgb, var(--bg) 90%, transparent)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Link
          to="/home"
          style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5, textDecoration: 'none', color: 'var(--ink)' }}
        >
          <span className="serif" style={{ fontSize: 24, fontStyle: 'italic', fontWeight: 500, letterSpacing: -0.4 }}>wydm</span>
          <span style={{ width: 5, height: 5, borderRadius: 5, background: 'var(--coral)' }} />
        </Link>
        {premium && <PremiumBadge />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={onOpenSearch}
          aria-label="Search profiles"
          title="Search"
          style={{
            width: 36,
            height: 36,
            borderRadius: 36,
            background: 'var(--surface)',
            border: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink)',
            boxShadow: 'var(--shadow-card)',
            cursor: 'pointer',
          }}
        >
          <Icons.search size={17} />
        </button>
        <Link
          to="/notifications"
          aria-label={`Notifications${unread > 0 ? ` — ${unread} unread` : ''}`}
          style={{
            position: 'relative',
            width: 36,
            height: 36,
            borderRadius: 36,
            background: 'var(--surface)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <Icons.bell size={17} />
          {unread > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                borderRadius: 100,
                background: 'var(--coral)',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Link>
      </div>
    </header>
  );
}

// MobileTabBar — fixed bottom nav with the five primary destinations.
// Sits on top of every signed-in page on phones. The mobile main area
// gets bottom padding to clear it (.app-shell-main on mobile).
function MobileTabBar({ counts }: { counts: UnreadCounts }) {
  const tabs: { to: string; icon: (p: { size?: number }) => JSX.Element; label: string; badge?: number }[] = [
    { to: '/home',     icon: Icons.home,  label: 'Home' },
    { to: '/discover', icon: Icons.compass, label: 'Discover' },
    { to: '/ratings',  icon: Icons.star,  label: 'Ratings' },
    { to: '/dm',       icon: Icons.chat,  label: 'Messages', badge: counts.dm },
    { to: '/settings', icon: Icons.gear,  label: 'Settings' },
  ];
  return (
    <nav
      className="mobile-tabbar"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: 25,
        display: 'none',
        background: 'var(--surface)',
        borderRadius: 100,
        boxShadow: 'var(--shadow-lift)',
        padding: '8px 14px',
        justifyContent: 'space-around',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {tabs.map((t) => <MobileTab key={t.to} {...t} />)}
    </nav>
  );
}

function MobileTab({
  to, icon: I, label, badge,
}: { to: string; icon: (p: { size?: number }) => JSX.Element; label: string; badge?: number }) {
  const loc = useLocation();
  const active = to === '/home' ? loc.pathname === '/home' : loc.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      aria-label={label}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        padding: '6px 10px',
        borderRadius: 100,
        color: active ? 'var(--coral)' : 'var(--ink-3)',
        textDecoration: 'none',
        flex: 1,
        position: 'relative',
      }}
    >
      <I size={24} />
      {typeof badge === 'number' && badge > 0 && (
        <span
          style={{
            position: 'absolute',
            top: 2,
            right: '50%',
            transform: 'translateX(14px)',
            minWidth: 14,
            height: 14,
            padding: '0 4px',
            borderRadius: 100,
            background: 'var(--coral)',
            color: '#fff',
            fontSize: 9,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  );
}

function Sidebar({ user, counts, onOpenSearch }: { user: User; counts: UnreadCounts; onOpenSearch: () => void }) {
  // Unread badge split: DMs land on the Messages row; everything else
  // on the Notifications bell row. Source of truth is the shared
  // useUnreadCounts hook on AppShell.
  const items: { to: string; icon: (p: { size?: number }) => JSX.Element; label: string; badge?: number }[] = [
    { to: '/home',          icon: Icons.home,  label: 'Home' },
    { to: '/discover',      icon: Icons.compass, label: 'Discover' },
    { to: '/ratings',       icon: Icons.star,  label: 'Ratings' },
    { to: '/dm',            icon: Icons.chat,  label: 'Messages', badge: counts.dm },
    { to: '/bookmarks',     icon: Icons.bookmark, label: 'Keepers' },
    { to: '/notifications', icon: Icons.bell,  label: 'Notifications', badge: counts.other },
    { to: '/settings',      icon: Icons.gear,  label: 'Settings' },
  ];
  const premium = user.tier === 'premium';

  return (
    <aside
      style={{
        borderRight: '1px solid var(--line)',
        padding: '24px 18px',
        background: 'var(--bg-2)',
        position: 'sticky',
        top: 0,
        alignSelf: 'start',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Brand + (premium only) status badge underneath */}
      <Link
        to="/home"
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 6,
          padding: '4px 8px',
          textDecoration: 'none',
          color: 'var(--ink)',
        }}
      >
        <div className="serif" style={{ fontSize: 32, fontStyle: 'italic', fontWeight: 500, letterSpacing: -0.5 }}>wydm</div>
        <div style={{ width: 7, height: 7, borderRadius: 7, background: 'var(--coral)' }} />
      </Link>
      {premium && <PremiumBadge style={{ marginLeft: 8, marginTop: 6, marginBottom: 22 }} />}
      <div style={{ height: premium ? 0 : 28 }} />

      {/* Nav */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Search opens a modal, not a route — styled to match the
            NavItem rows so it slots into the column without breaking
            the visual rhythm. */}
        <SearchNavItem onClick={onOpenSearch} />
        {items.map((it) => (
          <NavItem
            key={it.to}
            to={it.to}
            icon={<it.icon size={18} />}
            label={it.label}
            badge={it.badge}
          />
        ))}
        {/* Sign out — styled as a sibling of the nav items so the
            destinations + the lifecycle action all live in one column.
            The Settings page also has a Sign-out row for now; either
            entry point clears the session + bounces to /. */}
        <SignOutNavItem />
      </div>

      <div style={{ flex: 1 }} />
    </aside>
  );
}

// Small inline pill stating the user is on Premium. No link — it's a
// status indicator, not a CTA. Used in both the sidebar (under the
// wordmark) and the mobile topbar (to the right of the wordmark).
function PremiumBadge({ style }: { style?: React.CSSProperties }) {
  return (
    <div
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 9px',
        borderRadius: 100,
        background: 'var(--coral-soft)',
        color: 'var(--coral-ink)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        width: 'fit-content',
        ...style,
      }}
    >
      <Icons.check size={10} stroke={3.4} /> Premium
    </div>
  );
}

function SearchNavItem({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 12px',
        borderRadius: 12,
        background: 'transparent',
        color: 'var(--ink-2)',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <Icons.search size={18} />
      <span style={{ whiteSpace: 'nowrap', flex: 1 }}>Search</span>
    </button>
  );
}

// Styled as a NavItem-shaped button (icon + label) so the sidebar reads
// as one continuous list of actions. Sign-out is gated through a
// confirm dialog (see SignOutConfirm) to stop accidental clicks.
function SignOutNavItem() {
  const confirm = useSignOutConfirm();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
    <button
      type="button"
      ref={triggerRef}
      onClick={confirm.request}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 12px',
        borderRadius: 12,
        background: 'transparent',
        color: 'var(--ink-2)',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <Icons.signout size={18} />
      <span style={{ whiteSpace: 'nowrap', flex: 1 }}>Sign out</span>
    </button>
    <SignOutConfirm open={confirm.open} onCancel={confirm.cancel} triggerRef={triggerRef} />
    </>
  );
}

function NavItem({
  to, icon, label, badge,
}: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  const loc = useLocation();
  // Treat /home as exact-match; everything else matches its prefix so
  // nested routes still highlight their tab.
  const active = to === '/home' ? loc.pathname === '/home' : loc.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 12px',
        borderRadius: 12,
        // Active state: coral ink + bold weight + a small coral dot
        // anchored on the right. No surface card / shadow — the
        // sidebar reads cleaner with colour + weight carrying the
        // active state instead of chrome.
        background: 'transparent',
        color: active ? 'var(--coral-ink)' : 'var(--ink-2)',
        fontSize: 14,
        fontWeight: active ? 700 : 500,
        transition: 'color 0.15s',
        textAlign: 'left',
        textDecoration: 'none',
      }}
    >
      {icon}
      <span style={{ whiteSpace: 'nowrap', flex: 1 }}>{label}</span>
      {badge != null && badge > 0 && (
        <span
          style={{
            minWidth: 18,
            height: 18,
            padding: '0 6px',
            borderRadius: 100,
            background: 'var(--coral)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {/* Active-tab dot — only renders when there's no badge competing
          for the slot. Anchors the eye on the right edge so the row
          reads "page X · active" without chrome. */}
      {active && (badge == null || badge === 0) && (
        <span
          aria-hidden
          style={{
            width: 7,
            height: 7,
            borderRadius: 7,
            background: 'var(--coral)',
            flexShrink: 0,
          }}
        />
      )}
    </NavLink>
  );
}

/**
 * AppTopbar — optional in-page top section for signed-in pages. Render
 * at the top of an Outlet'd page when you want the design's eyebrow +
 * 40px display title with optional right slot (eg. Share button).
 */
export function AppTopbar({
  title,
  eyebrow,
  right,
  children,
}: {
  title?: React.ReactNode;
  eyebrow?: string;
  right?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="app-topbar"
      style={{
        padding: '32px 40px 20px',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 24,
        flexWrap: 'wrap',
      }}
    >
      <div>
        {eyebrow && (
          <div
            className="mono"
            style={{
              fontSize: 11,
              color: 'var(--coral)',
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {eyebrow}
          </div>
        )}
        {title && (
          <h1
            className="serif"
            style={{
              fontSize: 40,
              fontWeight: 500,
              letterSpacing: -0.8,
              lineHeight: 1.05,
              margin: 0,
            }}
          >
            {title}
          </h1>
        )}
        {children}
      </div>
      {right}
    </div>
  );
}
